import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { PaperClipIcon, TrashIcon, ChevronDownIcon, PaperAirplaneIcon, ArrowDownTrayIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Button } from '../components/ui/Button';
import { ExportDropdownMenu } from '../components/ui/ExportDropdownMenu';
import { LoadingOverlay } from '../components/ui/LoadingOverlay';
import { documentsAPI, ProcessingJob } from '../services/api';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useActivitiesStore, SavedActivity, FileWithDocId } from '../store/activitiesStore';
import { useTranslation } from 'react-i18next';

// Helper function to generate UUIDs with fallback for browsers that don't support crypto.randomUUID
const generateUUID = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback implementation
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

export const ContractReview: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [activityName, setActivityName] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState<FileWithDocId[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<Set<number>>(new Set());
  const [selectedAction, setSelectedAction] = useState('');
  const [showActions, setShowActions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ProcessingJob | null>(null);
  const [question, setQuestion] = useState('');
  const [actionHistory, setActionHistory] = useState<Array<{ action: string; result: ProcessingJob; question?: string }>>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteTargetIndex, setDeleteTargetIndex] = useState<number | null>(null);

  // Activity management from store
  const { addActivity, updateActivity, currentActivityId, setCurrentActivityId, getActivityById } = useActivitiesStore();
  const [lastSavedActivityName, setLastSavedActivityName] = useState('');
  const [lastSavedActionHistoryLength, setLastSavedActionHistoryLength] = useState(0);
  const [isSaved, setIsSaved] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Clear current activity and reset form when navigating without a specific activity to load
  // or when explicitly requested via clearActivity flag
  useEffect(() => {
    const state = location.state as { loadActivityId?: string; clearActivity?: boolean } | null;
    if (!state?.loadActivityId || state?.clearActivity) {
      // Reset all form state for a fresh activity
      setCurrentActivityId(null);
      setActivityName('');
      setUploadedFiles([]);
      setSelectedFiles(new Set());
      setSelectedAction('');
      setResult(null);
      setQuestion('');
      setActionHistory([]);
      setLastSavedActivityName('');
      setLastSavedActionHistoryLength(0);
      setIsSaved(false);
      setHasUnsavedChanges(false);
    }
  }, [location.pathname, location.state]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset dropdown when no files are selected
  useEffect(() => {
    if (selectedFiles.size === 0) {
      setSelectedAction('');
      setShowActions(false);
    }
  }, [selectedFiles.size]);

  // Track unsaved changes based on name or history changes
  useEffect(() => {
    const nameChanged = activityName !== lastSavedActivityName;
    const historyChanged = actionHistory.length !== lastSavedActionHistoryLength;

    if (nameChanged || historyChanged) {
      setHasUnsavedChanges(true);
      setIsSaved(false);
    }
  }, [activityName, actionHistory.length, lastSavedActivityName, lastSavedActionHistoryLength]);

  // Load activity when navigating from sidebar
  useEffect(() => {
    const state = location.state as { loadActivityId?: string } | null;
    if (state?.loadActivityId) {
      const activity = getActivityById(state.loadActivityId);
      if (activity) {
        // Check for unsaved changes
        if (hasUnsavedChanges) {
          const confirmed = window.confirm(
            'You have unsaved changes. Do you want to discard them and load the selected activity?'
          );
          if (!confirmed) return;
        }

        // Load the activity
        setActivityName(activity.name);
        // Convert old File[] format to new FileWithDocId[] format if needed
        const filesWithDocIds = activity.uploadedFiles.map(file =>
          typeof file === 'object' && 'file' in file && 'documentId' in file
            ? file as FileWithDocId
            : { file: file as File, documentId: null }
        );
        setUploadedFiles(filesWithDocIds);
        setSelectedFiles(activity.selectedFiles);
        setActionHistory(activity.actionHistory);
        setCurrentActivityId(activity.id);
        setLastSavedActivityName(activity.name);
        setLastSavedActionHistoryLength(activity.actionHistory.length);
        setIsSaved(true);
        setHasUnsavedChanges(false);
      }
    }
  }, [location.state]);

  const actions = [
    { id: 'summarize', label: t('documents:actions.summarize') },
    { id: 'keypoints', label: t('documents:actions.keypoints') },
    { id: 'keywords', label: t('documents:actions.keywords') },
    { id: 'questions', label: t('documents:actions.questions') },
    { id: 'similar', label: t('documents:actions.similar') },
    { id: 'compare', label: t('documents:actions.compare') },
    { id: 'differences', label: t('documents:actions.differences') },
  ];

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files).map(file => ({
        file,
        documentId: null
      }));

      // Calculate the starting index for new files
      const startIndex = uploadedFiles.length;

      // Add new files to uploadedFiles
      setUploadedFiles((prev) => [...prev, ...newFiles]);

      // Auto-select the newly added files (preserve existing selections)
      setSelectedFiles((prevSelected) => {
        const newSelected = new Set(prevSelected);
        // Add indices for the newly uploaded files
        for (let i = 0; i < newFiles.length; i++) {
          newSelected.add(startIndex + i);
        }
        return newSelected;
      });
    }
  };

  const handleRemoveFile = async (index: number) => {
    const fileWithDocId = uploadedFiles[index];

    // If file has a documentId, delete it from the backend
    if (fileWithDocId?.documentId) {
      try {
        await documentsAPI.delete(fileWithDocId.documentId);
        console.log(`[DEBUG] Deleted document ${fileWithDocId.documentId} from backend`);
      } catch (error) {
        console.error('[DEBUG] Failed to delete document from backend:', error);
        // Continue with frontend deletion even if backend deletion fails
      }
    }

    // Remove from uploadedFiles
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));

    // Remove from selectedFiles if it was selected
    setSelectedFiles((prev) => {
      const newSet = new Set(prev);
      newSet.delete(index);
      // Adjust indices for files after the removed one
      const adjustedSet = new Set<number>();
      newSet.forEach(i => {
        if (i < index) {
          adjustedSet.add(i);
        } else if (i > index) {
          adjustedSet.add(i - 1);
        }
      });
      return adjustedSet;
    });
  };

  const toggleFileSelection = (index: number) => {
    setSelectedFiles((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const handleExecuteAction = async (action: string) => {
    if (!action || uploadedFiles.length === 0) return;

    // Validate file selection based on action type
    const selectedCount = selectedFiles.size;

    switch (action) {
      case 'summarize':
        if (selectedCount === 0) {
          alert('Please select one or more files for the Summarize Action');
          setSelectedAction(''); // Reset dropdown
          return;
        }
        break;
      case 'keypoints':
        if (selectedCount === 0) {
          alert('Please select one or more files for the Extract Key Points Action');
          setSelectedAction(''); // Reset dropdown
          return;
        }
        break;
      case 'keywords':
        if (selectedCount === 0) {
          alert('Please select one or more of the attached files for the Extract Keywords Action');
          setSelectedAction(''); // Reset dropdown
          return;
        }
        break;
      case 'similar':
        if (selectedCount === 0) {
          alert('Please select one or more files for the Similar Documents Action');
          setSelectedAction(''); // Reset dropdown
          return;
        }
        break;
      case 'questions':
        if (selectedCount === 0) {
          alert('Please select one or more of the attached files for the Answer Questions Action');
          setSelectedAction(''); // Reset dropdown
          return;
        }
        if (!question) {
          alert('Please enter a question');
          setSelectedAction(''); // Reset dropdown
          return;
        }
        break;
      case 'compare':
        if (selectedCount !== 2) {
          alert('Please select two of the attached files for the Compare Documents Action');
          setSelectedAction(''); // Reset dropdown
          return;
        }
        break;
      case 'differences':
        if (selectedCount !== 2) {
          alert('Please select two of the attached files for the Document Differences Action');
          setSelectedAction(''); // Reset dropdown
          return;
        }
        break;
    }

    setLoading(true);
    setResult(null);

    try {
      // Get the selected files
      const selectedFilesList = Array.from(selectedFiles)
        .map(index => uploadedFiles[index])
        .filter(item => item !== undefined);

      if (selectedFilesList.length === 0) {
        alert('No files selected. Please try again.');
        setLoading(false);
        setSelectedAction('');
        return;
      }

      // Clear session files before uploading to ensure only the currently
      // selected files are in the session (prevents duplicate processing)
      console.log('[DEBUG] Clearing session files before action');
      try {
        await documentsAPI.clearSessionFiles();
        console.log('[DEBUG] Session files cleared successfully');
      } catch (clearError) {
        console.error('[DEBUG] Failed to clear session files:', clearError);
        // Continue anyway - the upload might still work
      }

      // Upload only the selected files
      // Note: We always re-upload to ensure session is properly synchronized
      const filesToUpload = selectedFilesList.map(item => item.file);
      console.log(`[DEBUG] Uploading ${filesToUpload.length} selected files`);
      const uploadJobs = await documentsAPI.upload(filesToUpload);
      const documentIds = uploadJobs.map(job => job.document_id);

      // Store the document IDs back into the uploadedFiles state
      setUploadedFiles(prev => {
        const updated = [...prev];
        Array.from(selectedFiles).forEach((index, i) => {
          if (updated[index] && uploadJobs[i]) {
            updated[index] = {
              ...updated[index],
              documentId: uploadJobs[i].document_id
            };
          }
        });
        return updated;
      });

      if (documentIds.length === 0) {
        alert('Failed to upload files. Please try again.');
        setLoading(false);
        setSelectedAction('');
        return;
      }

      // Use the first selected document's ID for the action
      const documentId = documentIds[0];

      // Execute selected action
      let actionResult: ProcessingJob;
      switch (action) {
        case 'summarize':
          actionResult = await documentsAPI.summarize(documentId);
          break;
        case 'keypoints':
          actionResult = await documentsAPI.extractKeyPoints(documentId);
          break;
        case 'keywords':
          actionResult = await documentsAPI.extractKeywords(documentId);
          break;
        case 'similar':
          actionResult = await documentsAPI.findSimilarDocuments(documentId);
          break;
        case 'questions':
          actionResult = await documentsAPI.answerQuestions(documentId, question);
          break;
        case 'compare':
          actionResult = await documentsAPI.compareDocuments(documentId);
          break;
        case 'differences':
          actionResult = await documentsAPI.findDifferences(documentId);
          break;
        default:
          setLoading(false);
          return;
      }

      setResult(actionResult);
      // Add to history
      const historyItem = {
        action,
        result: actionResult,
        ...(action === 'questions' && question && { question })
      };
      const updatedHistory = [...actionHistory, historyItem];
      setActionHistory(updatedHistory);

      // Auto-save the activity (generate name from files if needed)
      let generatedName = activityName;
      if (!generatedName.trim() && uploadedFiles.length > 0) {
        const fileNames = uploadedFiles.map(f => f.file.name).join(', ');
        generatedName = `Review: ${fileNames}`;
        setActivityName(generatedName);
      }

      if (generatedName.trim()) {
        const savedActivity: SavedActivity = {
          id: currentActivityId || generateUUID(),
          name: generatedName,
          uploadedFiles: [...uploadedFiles],
          selectedFiles: new Set(selectedFiles),
          actionHistory: updatedHistory as any,
          savedAt: new Date(),
          activityType: 'contract-review',
        };

        if (currentActivityId) {
          updateActivity(savedActivity);
        } else {
          addActivity(savedActivity);
          setCurrentActivityId(savedActivity.id);
        }

        // Update tracking variables to mark as saved
        setLastSavedActionHistoryLength(updatedHistory.length);
        setIsSaved(true);
        setHasUnsavedChanges(false);
      }
    } catch (error) {
      console.error('Error executing action:', error);
      alert('Failed to execute action. Please try again.');
      setSelectedAction(''); // Reset dropdown on error
    } finally {
      setLoading(false);
    }
  };

  const handleActionSelect = (actionId: string) => {
    setSelectedAction(actionId);
    setShowActions(false);
    // Don't auto-execute for questions action - wait for user to submit question
    if (actionId !== 'questions') {
      handleExecuteAction(actionId);
    }
  };

  const handleClearAction = () => {
    setSelectedAction('');
    setResult(null);
    setQuestion('');
    setActionHistory([]);
  };

  const handleSubmitQuestion = () => {
    if (question.trim()) {
      handleExecuteAction('questions');
    }
  };

  const handleQuestionKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Submit on Enter (without Shift)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmitQuestion();
    }
  };

  const handleSaveActivity = () => {
    if (!activityName.trim()) return;

    const activityId = currentActivityId || generateUUID();
    const nameChanged = lastSavedActivityName && lastSavedActivityName !== activityName;

    const newActivity: SavedActivity = {
      id: nameChanged ? generateUUID() : activityId,
      name: activityName,
      uploadedFiles: [...uploadedFiles],
      selectedFiles: new Set(selectedFiles),
      actionHistory: [...actionHistory],
      savedAt: new Date(),
      activityType: 'contract-review',
    };

    // If name changed, create new activity; otherwise update existing
    if (nameChanged || !currentActivityId) {
      addActivity(newActivity);
    } else {
      updateActivity(newActivity);
    }

    setCurrentActivityId(newActivity.id);
    setLastSavedActivityName(activityName);
    setLastSavedActionHistoryLength(actionHistory.length);
    setIsSaved(true);
    setHasUnsavedChanges(false);
  };

  const handleExport = (index: number) => async (format: 'word' | 'pdf' | 'text') => {
    await exportSingleResult(index, format);
  };

  const handlePrint = (index: number) => () => {
    printSingleResult(index);
  };

  const handleDeleteClick = (index: number) => {
    setDeleteTargetIndex(index);
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = () => {
    if (deleteTargetIndex === null) return;

    const reversedIndex = actionHistory.length - 1 - deleteTargetIndex;
    const newHistory = actionHistory.filter((_, idx) => idx !== reversedIndex);

    setActionHistory(newHistory);
    setShowDeleteDialog(false);
    setDeleteTargetIndex(null);

    // Update the activity in store
    if (currentActivityId) {
      const activity: SavedActivity = {
        id: currentActivityId,
        name: activityName,
        uploadedFiles,
        selectedFiles,
        actionHistory: newHistory,
        savedAt: new Date(),
        activityType: 'contract-review'
      };
      updateActivity(activity);
    }
  };

  const printSingleResult = (index: number) => {
    const reversedIndex = actionHistory.length - 1 - index;
    const item = actionHistory[reversedIndex];
    if (!item) return;

    const actionLabel = actions.find((a) => a.id === item.action)?.label || item.action;

    // Create a temporary div to render the markdown to HTML
    const tempDiv = document.createElement('div');
    tempDiv.style.display = 'none';
    document.body.appendChild(tempDiv);

    // Use ReactDOM to render the markdown
    import('react-dom/client').then(({ createRoot }) => {
      const root = createRoot(tempDiv);
      root.render(
        <ReactMarkdown remarkPlugins={[remarkGfm]}>
          {item.result.result || 'No result available'}
        </ReactMarkdown>
      );

      // Wait for render to complete
      setTimeout(() => {
        const renderedHtml = tempDiv.innerHTML;

        // Clean up
        root.unmount();
        document.body.removeChild(tempDiv);

        // Open print window
        const printWindow = window.open('', '_blank');
        if (!printWindow) return;

        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <title>${actionLabel} - ${activityName || 'Contract Review'}</title>
              <style>
                body {
                  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
                  line-height: 1.6;
                  color: #1f2937;
                  max-width: 800px;
                  margin: 0 auto;
                  padding: 40px 20px;
                }
                h1 { font-size: 24px; font-weight: bold; margin-bottom: 8px; margin-top: 24px; }
                h2 { font-size: 20px; font-weight: bold; margin-top: 20px; margin-bottom: 12px; }
                h3 { font-size: 18px; font-weight: 600; margin-top: 16px; margin-bottom: 8px; }
                h4 { font-size: 16px; font-weight: 600; margin-top: 12px; margin-bottom: 8px; }
                p { margin-bottom: 12px; line-height: 1.6; }
                ul, ol { margin-left: 24px; margin-bottom: 12px; }
                li { margin-bottom: 4px; line-height: 1.6; }
                strong { font-weight: 600; color: #111827; }
                em { font-style: italic; }
                code { background-color: #f3f4f6; padding: 2px 6px; border-radius: 3px; font-family: 'Courier New', monospace; font-size: 14px; }
                pre { background-color: #1f2937; color: #f3f4f6; padding: 12px; border-radius: 6px; overflow-x: auto; margin: 12px 0; }
                pre code { background-color: transparent; padding: 0; color: #f3f4f6; }
                blockquote { border-left: 4px solid #60a5fa; padding-left: 16px; font-style: italic; margin: 12px 0; color: #374151; }
                table { border-collapse: collapse; width: 100%; margin: 12px 0; }
                th { background-color: #f3f4f6; padding: 8px 12px; text-align: left; font-weight: 600; border-bottom: 1px solid #d1d5db; }
                td { padding: 8px 12px; border-top: 1px solid #e5e7eb; }
                hr { margin: 24px 0; border: none; border-top: 1px solid #d1d5db; }
                a { color: #2563eb; text-decoration: underline; }
                .header { margin-bottom: 32px; padding-bottom: 16px; border-bottom: 2px solid #d1d5db; }
                .activity-name { font-size: 14px; color: #6b7280; margin-bottom: 4px; }
                .action-title { font-size: 20px; font-weight: bold; color: #111827; }
                @media print {
                  body { padding: 20px; }
                }
              </style>
            </head>
            <body>
              <div class="header">
                ${activityName ? `<div class="activity-name">Activity: ${activityName}</div>` : ''}
                <div class="action-title">${actionLabel}</div>
              </div>
              <div class="content">
                ${renderedHtml}
              </div>
            </body>
          </html>
        `);

        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
        printWindow.close();
      }, 100);
    });
  };

  const printAllResults = async () => {
    if (actionHistory.length === 0) return;

    // Create temporary div for rendering all results
    const tempDiv = document.createElement('div');
    tempDiv.style.display = 'none';
    document.body.appendChild(tempDiv);

    try {
      // Dynamic import of react-dom/client
      const { createRoot } = await import('react-dom/client');

      // Render all markdown results
      const renderedResults: string[] = [];

      for (const item of [...actionHistory].reverse()) {
        const actionLabel = actions.find((a) => a.id === item.action)?.label || item.action;

        // Create a container for this result
        const resultContainer = document.createElement('div');
        tempDiv.appendChild(resultContainer);

        const root = createRoot(resultContainer);
        root.render(
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {item.result.result || 'No result available'}
          </ReactMarkdown>
        );

        // Wait for render to complete
        await new Promise(resolve => setTimeout(resolve, 100));

        const renderedHtml = resultContainer.innerHTML;
        renderedResults.push(`
          <div class="result-section">
            <h2 class="action-title">${actionLabel}</h2>
            <div class="content">
              ${renderedHtml}
            </div>
          </div>
        `);

        // Clean up this result's root
        root.unmount();
        tempDiv.removeChild(resultContainer);
      }

      // Clean up temp div
      document.body.removeChild(tempDiv);

      const resultsHtml = renderedResults.join('<hr class="section-divider">');

      // Open print window
      const printWindow = window.open('', '_blank');
      if (!printWindow) return;

      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>${activityName || 'Contract Review'} - All Results</title>
            <style>
              body {
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
                line-height: 1.6;
                color: #1f2937;
                max-width: 800px;
                margin: 0 auto;
                padding: 40px 20px;
              }
              h1 { font-size: 24px; font-weight: bold; margin-bottom: 8px; margin-top: 24px; }
              h2 { font-size: 20px; font-weight: bold; margin-top: 20px; margin-bottom: 12px; }
              h3 { font-size: 18px; font-weight: 600; margin-top: 16px; margin-bottom: 8px; }
              h4 { font-size: 16px; font-weight: 600; margin-top: 12px; margin-bottom: 8px; }
              p { margin-bottom: 12px; line-height: 1.6; }
              ul, ol { margin-left: 24px; margin-bottom: 12px; }
              li { margin-bottom: 4px; line-height: 1.6; }
              strong { font-weight: 600; color: #111827; }
              em { font-style: italic; }
              code { background-color: #f3f4f6; padding: 2px 6px; border-radius: 3px; font-family: 'Courier New', monospace; font-size: 14px; }
              pre { background-color: #1f2937; color: #f3f4f6; padding: 12px; border-radius: 6px; overflow-x: auto; margin: 12px 0; }
              pre code { background-color: transparent; padding: 0; color: #f3f4f6; }
              blockquote { border-left: 4px solid #60a5fa; padding-left: 16px; font-style: italic; margin: 12px 0; color: #374151; }
              table { border-collapse: collapse; width: 100%; margin: 12px 0; }
              th { background-color: #f3f4f6; padding: 8px 12px; text-align: left; font-weight: 600; border-bottom: 1px solid #d1d5db; }
              td { padding: 8px 12px; border-top: 1px solid #e5e7eb; }
              hr { margin: 24px 0; border: none; border-top: 1px solid #d1d5db; }
              a { color: #2563eb; text-decoration: underline; }
              .header { margin-bottom: 32px; padding-bottom: 16px; border-bottom: 2px solid #d1d5db; }
              .activity-name { font-size: 28px; font-weight: bold; color: #111827; margin-bottom: 8px; }
              .result-count { font-size: 14px; color: #6b7280; }
              .result-section { margin-bottom: 40px; page-break-inside: avoid; }
              .action-title { font-size: 18px; font-weight: bold; color: #111827; margin-bottom: 16px; }
              .section-divider { margin: 40px 0; }
              @media print {
                body { padding: 20px; }
                .result-section { page-break-inside: avoid; }
              }
            </style>
          </head>
          <body>
            <div class="header">
              <div class="activity-name">${activityName || 'Contract Review'}</div>
              <div class="result-count">${actionHistory.length} Result${actionHistory.length !== 1 ? 's' : ''}</div>
            </div>
            ${resultsHtml}
          </body>
        </html>
      `);

      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    } catch (error) {
      console.error('Error printing all results:', error);
      document.body.removeChild(tempDiv);
    }
  };

  const exportSingleResult = async (index: number, format: 'word' | 'text' | 'pdf') => {
    const reversedIndex = actionHistory.length - 1 - index;
    const item = actionHistory[reversedIndex];
    if (!item) return;

    const actionLabel = actions.find((a) => a.id === item.action)?.label || item.action;
    const title = `${actionLabel} - ${activityName || 'Contract Review'}`;
    const content = item.result.result || 'No result available';

    await exportDocument(content, title, format);
  };

  const exportAllResults = async (format: 'word' | 'text' | 'pdf') => {
    if (actionHistory.length === 0) return;

    // Combine all results into a single document
    const sections: string[] = [];

    for (const item of [...actionHistory].reverse()) {
      const actionLabel = actions.find((a) => a.id === item.action)?.label || item.action;
      const content = item.result.result || 'No result available';

      sections.push(`# ${actionLabel}\n\n${content}`);
    }

    const combinedContent = sections.join('\n\n---\n\n');
    const title = `${activityName || 'Contract Review'} - All Results`;

    await exportDocument(combinedContent, title, format);
  };

  const exportDocument = async (content: string, title: string, format: 'word' | 'text' | 'pdf') => {
    try {
      const extension = format === 'word' ? 'docx' : format === 'pdf' ? 'pdf' : 'txt';
      const filename = `${title}.${extension}`;

      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const response = await fetch(`${API_BASE_URL}/export/document`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          content,
          title,
          format: extension
        })
      });

      if (!response.ok) {
        throw new Error(`Export failed: ${response.statusText}`);
      }

      // Create blob from response
      const blob = await response.blob();

      // Try to use File System Access API (Chrome/Edge) for "Save As" dialog
      if ('showSaveFilePicker' in window) {
        try {
          const mimeTypes: Record<string, string> = {
            docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            pdf: 'application/pdf',
            txt: 'text/plain'
          };

          const handle = await (window as any).showSaveFilePicker({
            suggestedName: filename,
            types: [{
              description: format === 'word' ? 'Word Document' : format === 'pdf' ? 'PDF Document' : 'Text File',
              accept: { [mimeTypes[extension]]: [`.${extension}`] }
            }]
          });

          const writable = await handle.createWritable();
          await writable.write(blob);
          await writable.close();
          return; // Success, exit function
        } catch (err: any) {
          // User cancelled or API not available, fall back to standard download
          if (err.name !== 'AbortError') {
            console.warn('File System Access API failed, falling back to standard download:', err);
          } else {
            // User cancelled the save dialog
            return;
          }
        }
      }

      // Fallback: Standard download (goes to Downloads folder)
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export document. Please try again.');
    }
  };

  return (
    <div className="flex flex-col items-center h-full p-8">
      {/* Loading Overlay for document processing */}
      <LoadingOverlay
        isVisible={loading}
        message={t('documents:loading.processing', 'Processing...')}
        subMessage={t('documents:loading.pleaseWait', 'Analyzing your documents')}
      />

      {/* Header */}
      <div className="mb-8">
        <img
          src="/scales-logo.png"
          alt="Scales of Justice"
          className="h-24 w-24 mx-auto mb-4"
        />
        <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100">{t('documents:title')}</h1>
      </div>

      {/* Form */}
      <div className="w-full max-w-3xl space-y-6">
        {/* Attach Files Button */}
        <div>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleFileSelect}
            className="hidden"
            accept=".pdf,.docx,.doc,.txt,.rtf,.csv,.xls,.xlsx,.ods,.pptx"
          />
          <Button
            onClick={() => fileInputRef.current?.click()}
            fullWidth
            className="flex items-center justify-center space-x-2"
          >
            <PaperClipIcon className="h-5 w-5" />
            <span>{t('documents:attachFiles')}</span>
          </Button>
        </div>

        {/* Files List */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('documents:files')}</label>
          <div className="border border-gray-300 dark:border-gray-600 rounded-lg min-h-[200px] p-4 bg-white dark:bg-gray-700">
            {uploadedFiles.length === 0 ? (
              <div className="flex items-center justify-center h-full text-gray-400 dark:text-gray-500">
                {t('documents:noFilesSelected')}
              </div>
            ) : (
              <div className="space-y-2">
                {uploadedFiles.map((fileWithDocId, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-500 transition-colors"
                  >
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        checked={selectedFiles.has(index)}
                        onChange={() => toggleFileSelection(index)}
                        className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                      />
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {fileWithDocId.file.name}
                      </span>
                    </div>
                    <button
                      onClick={() => handleRemoveFile(index)}
                      className="text-gray-400 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Action Dropdown */}
        <div className="relative">
          <button
            onClick={() => setShowActions(!showActions)}
            disabled={selectedFiles.size === 0}
            className="w-full px-4 py-3 text-left bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:cursor-not-allowed flex items-center justify-between"
          >
            <span className={selectedAction ? 'text-gray-900 dark:text-gray-100' : 'text-gray-500 dark:text-gray-400'}>
              {selectedAction
                ? actions.find((a) => a.id === selectedAction)?.label
                : t('documents:action')}
            </span>
            <ChevronDownIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
          </button>

          {showActions && selectedFiles.size > 0 && (
            <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg">
              {actions.map((action) => {
                const requiresTwo = action.id === 'compare' || action.id === 'differences';
                const isDisabled = requiresTwo && uploadedFiles.length < 2;

                return (
                  <button
                    key={action.id}
                    onClick={() => {
                      if (!isDisabled) {
                        handleActionSelect(action.id);
                      }
                    }}
                    disabled={isDisabled}
                    className={`w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors first:rounded-t-lg last:rounded-b-lg ${
                      selectedAction === action.id ? 'text-blue-600 dark:text-blue-400 font-medium' : 'text-gray-700 dark:text-gray-300'
                    } ${isDisabled ? 'text-gray-400 dark:text-gray-500 cursor-not-allowed' : ''}`}
                  >
                    {action.label}
                    {isDisabled && ' ' + t('documents:requiresTwoDocuments')}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Question Input (shown when "Answer Questions" is selected) */}
        {selectedAction === 'questions' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('legal:yourQuestion')}:
            </label>
            <div className="relative">
              <textarea
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                onKeyDown={handleQuestionKeyDown}
                className="w-full px-4 py-3 pr-12 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none resize-none placeholder-gray-400 dark:placeholder-gray-500"
                rows={3}
                placeholder="Enter your question about the document..."
                disabled={loading}
              />
              <button
                onClick={handleSubmitQuestion}
                disabled={loading || !question.trim()}
                className="absolute bottom-3 right-3 p-2 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 disabled:text-gray-400 dark:disabled:text-gray-500 disabled:cursor-not-allowed transition-colors"
                title="Submit question (Enter)"
              >
                <PaperAirplaneIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        )}

        {/* Clear Action Button */}
        {(selectedAction || result) && (
          <Button
            onClick={handleClearAction}
            variant={loading ? 'secondary' : 'primary'}
            fullWidth
            disabled={loading}
          >
            {loading ? t('documents:processing') : t('documents:clearAction')}
          </Button>
        )}

        {/* Results History Display */}
        {actionHistory.length > 0 && (
          <div className="mt-8 space-y-6">
            {[...actionHistory].reverse().map((item, index) => (
              <div key={index} className="border-t border-gray-200 dark:border-gray-700 pt-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                    {t('documents:actionLabel')} {actions.find((a) => a.id === item.action)?.label}
                  </h3>
                  <div className="flex items-center gap-3">
                    <ExportDropdownMenu
                      onExport={handleExport(index)}
                      onPrint={handlePrint(index)}
                      showPrint={true}
                    />
                    <button
                      onClick={() => handleDeleteClick(index)}
                      className="text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                      title="Delete this result"
                    >
                      <TrashIcon className="h-6 w-6" />
                    </button>
                  </div>
                </div>

                {item.result.status === 'finished' && item.result.result && (
                  <div className="p-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                    <div className="prose prose-sm md:prose-base lg:prose-lg max-w-none text-gray-800 dark:text-gray-200">
                      {item.question && (
                        <div className="mb-4 text-gray-900 dark:text-gray-100">
                          <span className="font-bold">Question:</span>{' '}
                          <span className="italic">{item.question}</span>
                        </div>
                      )}
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                          h1: ({node, ...props}) => <h1 className="text-2xl font-bold mt-6 mb-4 text-gray-900 dark:text-gray-100" {...props} />,
                          h2: ({node, ...props}) => <h2 className="text-xl font-bold mt-5 mb-3 text-gray-900 dark:text-gray-100" {...props} />,
                          h3: ({node, ...props}) => <h3 className="text-lg font-semibold mt-4 mb-2 text-gray-900 dark:text-gray-100" {...props} />,
                          h4: ({node, ...props}) => <h4 className="text-base font-semibold mt-3 mb-2 text-gray-900 dark:text-gray-100" {...props} />,
                          p: ({node, ...props}) => <p className="mb-3 leading-relaxed text-gray-800 dark:text-gray-200" {...props} />,
                          ul: ({node, ...props}) => <ul className="list-disc list-outside ml-6 mb-3 space-y-1" {...props} />,
                          ol: ({node, ...props}) => <ol className="list-decimal list-outside ml-6 mb-3 space-y-1" {...props} />,
                          li: ({node, ...props}) => <li className="leading-relaxed text-gray-800 dark:text-gray-200" {...props} />,
                          strong: ({node, ...props}) => <strong className="font-semibold text-gray-900 dark:text-gray-100" {...props} />,
                          em: ({node, ...props}) => <em className="italic text-gray-800 dark:text-gray-200" {...props} />,
                          code: ({node, ...props}: any) => {
                            const isInline = !props.className;
                            return isInline
                              ? <code className="px-1.5 py-0.5 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded text-sm font-mono" {...props} />
                              : <code className="block p-3 bg-gray-800 dark:bg-gray-900 text-gray-100 rounded overflow-x-auto text-sm font-mono" {...props} />;
                          },
                          blockquote: ({node, ...props}) => <blockquote className="border-l-4 border-blue-400 dark:border-blue-600 pl-4 italic my-3 text-gray-800 dark:text-gray-200" {...props} />,
                          table: ({node, ...props}) => <table className="min-w-full divide-y divide-gray-300 dark:divide-gray-600 my-3" {...props} />,
                          th: ({node, ...props}) => <th className="px-3 py-2 bg-gray-100 dark:bg-gray-700 text-left text-sm font-semibold text-gray-900 dark:text-gray-100" {...props} />,
                          td: ({node, ...props}) => <td className="px-3 py-2 text-sm text-gray-700 dark:text-gray-300 border-t border-gray-200 dark:border-gray-600" {...props} />,
                          hr: ({node, ...props}) => <hr className="my-6 border-gray-300 dark:border-gray-600" {...props} />,
                          a: ({node, ...props}) => <a className="text-blue-600 dark:text-blue-400 hover:underline" {...props} />,
                        }}
                      >
                        {item.result.result}
                      </ReactMarkdown>
                    </div>
                  </div>
                )}

                {item.result.status === 'failed' && (
                  <div className="p-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                    <p className="text-red-800 dark:text-red-200">Action failed. Please try again.</p>
                  </div>
                )}

                {(item.result.status === 'pending' || item.result.status === 'running') && (
                  <div className="p-6 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <p className="text-gray-800 dark:text-gray-200">Processing... Please wait.</p>
                  </div>
                )}

                {!item.result.result && item.result.status === 'finished' && (
                  <div className="p-6 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                    <p className="text-yellow-800 dark:text-yellow-200">No result data available.</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        {showDeleteDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Delete Result
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Are you sure you want to delete this result? This action cannot be undone.
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={handleDeleteConfirm}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
                >
                  Delete
                </button>
                <button
                  onClick={() => {
                    setShowDeleteDialog(false);
                    setDeleteTargetIndex(null);
                  }}
                  className="flex-1 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
